import type {
  ShearAnchorCheckDTO,
  ShearAnchorConnectorSelectionDTO,
  ShearAnchorLoadsDTO,
  ShearAnchorPosition,
} from '@api/fastAPI.schemas'
import { SHEAR_ANCHOR_POSITIONS } from 'src/components/manager/shearConnections'
import { ConnectorSelection, PRIORITY_LIST_SELECTION } from '../../connectorSelection'

export type ShearLoadCategory = 'Wind' | 'EarthQuake'

export const SHEAR_LOAD_CATEGORIES: ShearLoadCategory[] = ['Wind', 'EarthQuake']

export const shearLoadCategoryLabel: Record<ShearLoadCategory, string> = {
  Wind: 'Wind',
  EarthQuake: 'Erdbeben',
}

const decimalFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Formats a value in N or N/m as kN resp. kN/m with 2 decimals (de-DE). */
export const formatKilo = (value: number | undefined | null): string =>
  value == null ? '–' : decimalFormatter.format(value / 1000)

/** Formats a length in metres with 2 decimals and a unit (de-DE). */
export const formatMeters = (meters: number | undefined | null): string =>
  meters == null ? '–' : `${decimalFormatter.format(meters)} m`

/** Formats a plain number (a length in m, a utilization) with 2 decimals (de-DE). */
export const formatNumber = (value: number | undefined | null): string =>
  value == null ? '–' : decimalFormatter.format(value)

/** The check of each load category, keyed for column lookup. */
export const checksByLoadCategory = (
  checks: ShearAnchorCheckDTO[],
): Partial<Record<ShearLoadCategory, ShearAnchorCheckDTO>> => {
  const out: Partial<Record<ShearLoadCategory, ShearAnchorCheckDTO>> = {}
  for (const check of checks) {
    if (check.load_category === 'Wind' || check.load_category === 'EarthQuake') {
      out[check.load_category] = check
    }
  }
  return out
}

/**
 * The checks of each anchor position that has any, in position order — a segment is
 * anchored at its foot and at its head, each with its own Schubverbinder.
 */
export const checksByPosition = (
  checks: ShearAnchorCheckDTO[],
): { position: ShearAnchorPosition; checks: ShearAnchorCheckDTO[] }[] =>
  SHEAR_ANCHOR_POSITIONS.map(position => ({
    position,
    checks: checks.filter(check => check.position === position),
  })).filter(entry => entry.checks.length > 0)

/** The check with the highest utilization — the one the list row and the badge show. */
export const decisiveShearCheck = (
  checks: ShearAnchorCheckDTO[],
): ShearAnchorCheckDTO | undefined =>
  checks.reduce<ShearAnchorCheckDTO | undefined>(
    (max, check) => (max == null || check.utilization > max.utilization ? check : max),
    undefined,
  )

/**
 * Stable guid of an Einwirkungen load row — matches the table rows with the scene loads so the
 * eye column can hide them. Falls back to a synthesized per-element key when the original load
 * carries no guid (clipped line loads get a fresh one).
 */
export const shearAnchorLoadGuid = (
  loadGuid: string | null | undefined,
  elementGuid: string,
  kind: string,
  index: number,
): string => loadGuid ?? `${elementGuid}-${kind}-${index}`

/**
 * Maps the vertical loads standing on a stiffening segment to scene `DomainLoad`s so they can be
 * drawn with `SimpleLoadMesh` while the shear anchor is selected — the same treatment the tensile
 * anchor's relieving loads get. The DTO carries the full original loads; the domain is resolved
 * via `guidToDomain`, and loads without a resolvable domain or load case are skipped.
 *
 * Line loads are the clipped share standing on the segment, so the scene shows exactly what the
 * Einwirkungen table lists and what the friction is computed from.
 */
export const getShearAnchorDomainLoads = (
  loads: ShearAnchorLoadsDTO | undefined,
  guidToDomain: Record<string, Domain | undefined>,
  elementGuid: string,
): DomainLoad[] => {
  if (!loads) return []

  const toLoadCase = (value: {
    category?: string
    load_case_type?: string
  }): LoadCase | undefined => {
    if (!value?.category || !value.load_case_type) return undefined
    return {
      identifier: value.load_case_type,
      category: value.category as LoadCategory,
      load_case_type: value.load_case_type as LoadCaseTypes,
    }
  }

  const domainLoads: DomainLoad[] = []

  loads.vertical_point_loads.forEach((contribution, index) => {
    const dto = contribution.original_load
    const domain = dto.domain_guid ? guidToDomain[dto.domain_guid] : undefined
    const loadCase = toLoadCase(contribution.load_case)
    if (!domain || !loadCase) return
    domainLoads.push({
      guid: shearAnchorLoadGuid(contribution.load_guid, elementGuid, 'vertical-point', index),
      element_guid: elementGuid,
      load_type: 'point-load',
      load_case: loadCase,
      force: dto.force,
      relative_position: Number(dto.relative_position),
      domain_guid: domain.guid,
      domain,
    })
  })

  loads.vertical_line_loads.forEach((contribution, index) => {
    const dto = contribution.original_load
    const domain = dto.domain_guid ? guidToDomain[dto.domain_guid] : undefined
    const loadCase = toLoadCase(contribution.load_case)
    if (!domain || !loadCase) return
    domainLoads.push({
      guid: shearAnchorLoadGuid(contribution.load_guid, elementGuid, 'vertical-line', index),
      element_guid: elementGuid,
      load_type: 'line-load',
      load_case: loadCase,
      start: dto.start,
      end: dto.end,
      relative_start: Number(dto.relative_start ?? 0),
      relative_end: Number(dto.relative_end ?? 1),
      domain_guid: domain.guid,
      domain,
    })
  })

  return domainLoads
}

/** The saved Schubverbinder selection of one position; the prioritization when none is stored. */
export const savedShearAnchorSelection = (
  selections: ShearAnchorConnectorSelectionDTO[],
  position: ShearAnchorPosition,
): ConnectorSelection => {
  const saved = selections.find(candidate => candidate.position === position)
  return saved?.connectorSelectionPolicy === 'UserDefined'
    ? { policy: 'UserDefined', connectorId: saved.userDefinedConnectorId ?? null }
    : PRIORITY_LIST_SELECTION
}
